import { useQuery } from '@apollo/client'
import { CROSS_SINGLE_AVERAGE_PRICE_REVENUE_QUERY, SOLITARY_SINGLE_QUERY } from 'query/query'
import { useAppliedFiltersAndContext } from 'hooks/useAppliedFiltersAndContext'
import { Schema, Filter, Operator } from 'typings/filters'
import { Data } from 'components/Charts/types'
import { AlertMessages } from 'util/errorMessages'
import { setStoriesSummaryError } from 'reducers/KBQ/storiesSummaryError'
import { useKBQSelectedValueByPath } from 'hooks/useKBQSelectedValueByPath'

export const MINIMUM_SAMPLE_SIZE = 50
export const PREVIOUS_PURCHASED_BRAND = 'previousBrand'
export const PREVIOUS_BRAND_VARIABLE = 21
export const PURCHASED_BRAND_VARIABLE = 14
export const BRAND_LOYALTY_VARIABLE = 835

const getLocalFilters = (defaultBrand?: any) => {
  const brandSwitchSummaryLocalFilters = defaultBrand
    ? [
        { variable_id: PREVIOUS_BRAND_VARIABLE, answer_code: defaultBrand?.answer_code } as Omit<
          Filter,
          'label'
        >,
        {
          variable_id: BRAND_LOYALTY_VARIABLE,
          answer_code: 1,
        } as Omit<Filter, 'label'>,
      ]
    : []
  const previousAndPurchasedBrandLocalFilters = defaultBrand
    ? [
        { variable_id: PREVIOUS_BRAND_VARIABLE, answer_code: defaultBrand?.answer_code } as Omit<
          Filter,
          'label'
        >,
        {
          variable_id: PURCHASED_BRAND_VARIABLE,
          answer_code: defaultBrand?.answer_code,
          operator: Operator.NOT_IN,
        } as Omit<Filter, 'label'>,
      ]
    : []
  const previousBrandFilter = defaultBrand
    ? [{ variable_id: PREVIOUS_BRAND_VARIABLE, answer_code: defaultBrand?.answer_code }]
    : []
  return {
    brandSwitchSummaryLocalFilters,
    previousBrandFilter,
    previousAndPurchasedBrandLocalFilters,
  }
}
export const useBrandSwitchSummary = () => {
  const { appliedFilters, context } = useAppliedFiltersAndContext()

  const { dispatch, defaultBrand } = useKBQSelectedValueByPath()
  const { brandSwitchSummaryLocalFilters, previousAndPurchasedBrandLocalFilters } =
    getLocalFilters(defaultBrand)
  const {
    data: brands,
    loading: brandsLoading,
    error: brandsError,
  } = useQuery<Data>(SOLITARY_SINGLE_QUERY, {
    variables: {
      primaryVar: 'previousBrand',
      filters: appliedFilters,
      context,
      localFilters: brandSwitchSummaryLocalFilters,
      schema: Schema.CIE,
      skip: !defaultBrand,
    },
  })
  const {
    data: purchasedBrands,
    loading: purchasedBrandsLoading,
    error: purchasedBrandsError,
  } = useQuery<Data>(SOLITARY_SINGLE_QUERY, {
    variables: {
      primaryVar: 'previousBrand',
      filters: appliedFilters,
      context,
      localFilters: previousAndPurchasedBrandLocalFilters,
      schema: Schema.CIE,
      skip: !defaultBrand,
    },
  })
  const {
    data: previousBrands,
    loading: previousBrandsLoading,
    error: previousBrandsError,
  } = useQuery<Data>(SOLITARY_SINGLE_QUERY, {
    variables: {
      primaryVar: 'previousBrand',
      filters: appliedFilters,
      context,
      localFilters: [
        { variable_id: 835, answer_code: 2 } as Omit<Filter, 'label'>,
        { variable_id: 835, answer_code: 1 } as Omit<Filter, 'label'>,
      ],
      schema: Schema.CIE,
      skip: !defaultBrand,
    },
  })
  const averagePriceLocalFilters = [
    { variable_id: PREVIOUS_BRAND_VARIABLE, answer_code: defaultBrand?.answer_code },
    { variable_id: BRAND_LOYALTY_VARIABLE, answer_code: 1 },
  ]
  const {
    data: averagePriceData,
    loading: averagePriceDataLoading,
    error: averagePriceDataError,
  } = useQuery<Data>(CROSS_SINGLE_AVERAGE_PRICE_REVENUE_QUERY, {
    variables: {
      primaryVar: 'purchasedBrand',
      secondaryVar: 'purchasePriceNSP',
      filters: appliedFilters,
      localFilters: averagePriceLocalFilters,
      context,
      schema: Schema.CIE,
    },
    skip: !defaultBrand,
  })
  const summaryNumbersLoading =
    brandsLoading || previousBrandsLoading || averagePriceDataLoading || purchasedBrandsLoading
  const hasSelectedBrand = brands?.cross?.series[0]?.dataPoints.find(
    (dp) => dp.secondaryValue === defaultBrand?.label
  )
  const previousSelectBrand = previousBrands?.cross?.series[0]?.dataPoints?.find(
    (dp) => dp.secondaryValue === defaultBrand?.label
  )

  let totalPurchasedBrand
  let totalUnWeightedPurchasedBrand
  let totalPrevAndPurchasedBrandCount

  if (hasSelectedBrand?.secondaryValue && brands && !summaryNumbersLoading && purchasedBrands) {
    totalPurchasedBrand = brands?.cross?.totalCount
    totalUnWeightedPurchasedBrand = brands?.cross?.totalCountUnweighted
    totalPrevAndPurchasedBrandCount = purchasedBrands?.cross?.totalCount
  }

  let totalBrandSwitchPercent

  if (
    totalPurchasedBrand &&
    hasSelectedBrand?.secondaryValue &&
    totalPrevAndPurchasedBrandCount &&
    previousSelectBrand
  ) {
    totalBrandSwitchPercent = Math.round(
      (totalPurchasedBrand / previousSelectBrand?.weighted) * 100
    )
  }
  const hasErrorOcurred =
    brandsError || previousBrandsError || averagePriceDataError || purchasedBrandsError

  const errorMessage =
    brandsError?.message || previousBrandsError?.message || averagePriceDataError?.message
  const hasForbiddenError = errorMessage === AlertMessages.FORBIDDEN_ERROR_MESSAGE

  let brandSwitchSummaryHasLowSample

  if (hasSelectedBrand?.secondaryValue && totalUnWeightedPurchasedBrand) {
    brandSwitchSummaryHasLowSample = totalUnWeightedPurchasedBrand < 50
  }

  const brandSwitchSummaryHasNoData =
    !hasSelectedBrand && !summaryNumbersLoading && !hasErrorOcurred

  if (hasErrorOcurred && !summaryNumbersLoading) {
    dispatch(setStoriesSummaryError({ hasError: true, hasForbiddenError }))
  } else {
    dispatch(setStoriesSummaryError({ hasError: false, hasForbiddenError: false }))
  }
  const averagePrice = averagePriceData?.cross?.summarySeries?.dataPoints.map(
    (item: any) => item.average
  )

  return {
    summaryNumbersLoading,
    averagePrice,
    totalBrandSwitchNumber: totalPurchasedBrand,
    totalBrandSwitchPercent: totalBrandSwitchPercent,
    brandSwitchSummaryHasLowSample: brandSwitchSummaryHasLowSample,
    brandSwitchSummaryHasNoData: brandSwitchSummaryHasNoData,
  }
}
